import { Injectable, inject } from '@angular/core';
import { TourService } from 'ngx-ui-tour-md-menu';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TourHelperService {
  private tourService = inject(TourService);
  isTourTriggered: boolean = false;
  private stepHideSubject = new Subject<void>();

  stepHide$ = this.stepHideSubject.asObservable();
  initNewGroupTour() {
    this.tourService.initialize([{
      anchorId: 'group-tab',
      stepId: 'group-tab',
      content: 'Click ‘Groups’ to create a group of suppliers for a specific program, or project.',
      title: 'Groups',
      nextStep: 'new-group',
      isAsync: true
    }, {
      anchorId: 'new-group',
      stepId: 'new-group',
      content: 'Click ‘NEW GROUP’ to create a new group',
      title: 'New Group',
      nextStep: 'skipStep',
      prevStep: 'group-tab',
      isAsync: true
    }, {
      anchorId: 'skipStep',
      stepId: 'skipStep',
      content: '',
      title: 'This step will not be visible in the UI',
      nextStep: 'group-name',
      isAsync: true
    },
    {
      anchorId: 'group-name',
      stepId: 'group-name',
      content: 'Input name of group',
      title: 'Name',
      prevStep: 'new-group',
      nextStep: 'group-description',
      isAsync: true
    }, {
      anchorId: 'group-description',
      stepId: 'group-description',
      content: `Input a brief description of the group.`,
      title: 'Description',
      prevStep: 'group-name',
      nextStep: 'add-new-group',
      isAsync: true
    }, {
      anchorId: 'add-new-group',
      stepId: 'add-new-group',
      content: `Click ADD NEW GROUP to complete the group creation.`,
      title: 'Add new group',
      prevStep: 'group-description',
      nextStep: 'group-success-toast',
      isAsync: true
    },
    {
      anchorId: 'group-success-toast',
      stepId: 'group-success-toast',
      content: `New group is created successfully. Close this toast to proceed to the next step.`,
      title: 'Group creation successful',
      prevStep: 'group-description',
      nextStep: 'edit_group',
      isAsync: true
    },
    {
      anchorId: 'edit_group',
      stepId: 'edit_group',
      content: `To update a group, click Edit. You can add or remove suppliers from the group.`,
      title: 'Edit group',
      prevStep: '',
      nextStep: 'add-suppliers',
      isAsync: true
    },
    {
      anchorId: 'add-suppliers',
      stepId: 'add-suppliers',
      content: `Click ‘Search and Add suppliers’ to search and add suppliers to the group from your network.`,
      title: 'Add Suppliers',
      prevStep: 'edit_group',
      isAsync: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
    this.setupGroupTourEventListeners();
  }

  setupAddToGroupSteps() {
    this.tourService.initialize([{
      anchorId: 'search-toggle',
      stepId: 'search-toggle',
      content: `Add a supplier from your organization's list of suppliers.`,
      title: 'My Suppliers',
      nextStep: 'search-criteria',
      isAsync: true
    },
    {
      anchorId: 'search-criteria',
      stepId: 'search-criteria',
      content: `Input one of the required search parameters like Organization name.`,
      title: 'Add Suppliers',
      prevStep: 'search-toggle',
      nextStep: 'complete-search',
      isAsync: true
    }, {
      anchorId: 'complete-search',
      stepId: 'complete-search',
      content: '<ol> <li>Click on the ‘Search’ to view list of matched suppliers.</li> <li> You can check supplier(s) from search results and add them to the group.</li></ol>',
      title: 'Search',
      enableBackdrop: true,
      prevStep: 'search-criteria',
      nextStep: 'select-suppliers'
    },
    {
      anchorId: 'select-suppliers',
      stepId: 'select-suppliers',
      content: `Check the suppliers(s) from the search list to add to the group`,
      title: 'Select Suppliers',
      prevStep: 'complete-search',
      nextStep: 'search-groups',
      isAsync: true
    },
    {
      anchorId: 'search-groups',
      stepId: 'search-groups',
      content: `Input the group name and select the group from the dropdown to add the checked supplier(s) to this group`,
      title: 'Search groups',
      prevStep: 'select-suppliers',
      nextStep: 'add-to-group-success',
      isAsync: true
    },
    {
      anchorId: 'add-to-group-success',
      stepId: 'add-to-group-success',
      content: `Supplier is successfully added to the group. You may now close this toast to end the tour.`,
      title: 'Success',
      prevStep: 'search-groups',
      isAsync: true
    }], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
  }

  setupGroupTourEventListeners() {
    this.tourService.stepShow$.subscribe((step) => {
      if (step.step.stepId === 'skipStep') {
        this.isTourTriggered = true;
        this.triggerYourAction('newGroup')
      }
    })
    this.tourService.stepHide$.subscribe((step) => {
      if (step.step.stepId === 'group-name' && step.direction == 1) {
        this.stepHideSubject.next();
      }
      if(step.step.stepId === 'add-new-group' && step.direction == 0) {
        this.triggerYourAction('createNewGroup');
      }
      if(step.step.stepId === 'group-success-toast' && step.direction == 0) {
        this.triggerYourAction('closeSuccessToast');
        this.tourService.prev();
      }
      if(step.step.stepId === 'edit_group' && step.direction == 0) {
        this.triggerYourAction('edit_group');
      }
    });
    this.tourService.start$.subscribe((step) => {
      this.setIsTourStartedIndicator(true);
    });
    this.tourService.events$.subscribe((eventRegistered: { name: string; value: any; }) => {
      //if tour is on search suppliers step and tour is ended dont reset flag, else reset
      if(eventRegistered.name === "stepHide" && (eventRegistered.value as any).step.anchorId == "add-suppliers") {
        this.setIsTourStartedIndicator(true);
      }
    });
  }

  initNewCampaignTourService() {
    this.tourService.initialize([{
      anchorId: 'campaign-tab',
      stepId: 'campaign-tab',
      content: 'Click ‘Campaign’ to bulk upload a list of existing or new suppliers into a spreadsheet and initiate actions on them',
      title: 'Campaign',
      nextStep: 'new-campaign',
      allowUserInitiatedNavigation: true
    }, {
      anchorId: 'new-campaign',
      stepId: 'new-campaign',
      content: 'Start a new Campaign by clicking here',
      title: 'New campaign',
      prevStep: 'campaign-tab',
      nextStep: 'campaign-name',
      allowUserInitiatedNavigation: true,
    },
    {
      anchorId: 'campaign-name',
      stepId: 'campaign-name',
      content: 'Enter a unique campaign name',
      title: 'Campaign name',
      nextStep: 'template-download',
      route: '/campaign/upload'
    }, {
      anchorId: 'template-download',
      stepId: 'template-download',
      content: `<ol> <li>Download the campaign template from this link.</li> 
      <li>This template contains the required data marked with an asterisk.</li>
      <li>The spreadsheet can contain up to 200 rows.</li>`,
      title: 'Download template',
      prevStep: 'campaign-name',
      nextStep: 'campaign-upload'
    }, {
      anchorId: 'campaign-upload',
      stepId: 'campaign-upload',
      content: `<ol> <li>Upload template.</li>
      <li>Upon successful upload, select continue. </li>
      <li>This will transfer you to the Campaign Dashboard, where you can proceed further with field validation.</li>`,
      title: 'Upload doc Template',
      prevStep: 'template-download'
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
  }

  initSearchSupplierTourService() {
    this.tourService.initialize([{
      anchorId: 'search-form',
      stepId: 'search-form',
      content: 'The Search section allows buyers to search for a supplier using specified input parameters',
      title: 'Search suppliers\' toolbar',
      enableBackdrop: true,
      nextStep: 'search-toggle',
      allowUserInitiatedNavigation: true
    }, {
      anchorId: 'search-toggle',
      stepId: 'search-toggle',
      content: 'You can look up suppliers in your network or Exostar network',
      title: 'Toggle',
      enableBackdrop: true,
      prevStep: 'search-form',
      nextStep: 'search-criteria',
      isAsync: true
    }, {
      anchorId: 'search-criteria',
      stepId: 'search-criteria',
      content: 'Input one of the required search parameters like Organization Name',
      title: 'Search parameters',
      enableBackdrop: true,
      prevStep: 'search-toggle',
      nextStep: 'complete-search',
      isAsync: true
    }, {
      anchorId: 'complete-search',
      stepId: 'complete-search',
      content: 'Click on the ‘Search’ to view list of matched suppliers. Once results are retrieved, you can view the supplier profile.',
      title: 'Search',
      enableBackdrop: true,
      prevStep: 'search-criteria',
      nextStep: 'open-supplier'
    }, {
      anchorId: 'open-supplier',
      stepId: 'open-supplier',
      content: 'You can view the supplier profile by clicking the icon ',
      title: 'Open view supplier',
      enableBackdrop: true,
      prevStep: 'complete-search',
      isAsync: true,
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
  }

  initHomeTour() {
    this.tourService.initialize([{
      anchorId: 'search-form',
      stepId: 'search-form',
      content: 'The Search section allows you to search for suppliers in your network or across all Exostar suppliers.',
      title: 'Search Suppliers',
      enableBackdrop: true,
      nextStep: 'search-toggle',
      allowUserInitiatedNavigation: true
    }, {
      anchorId: 'search-toggle',
      stepId: 'search-toggle',
      content: 'Use this toggle to switch between searching within your network of suppliers or across all Exostar suppliers.',
      title: 'Supplier Network Toggle',
      enableBackdrop: true,
      prevStep: 'search-form',
      nextStep: 'search-criteria',
      isAsync: true
    }, {
      anchorId: 'search-criteria',
      stepId: 'search-criteria',
      content: 'Enter an Organization Name or use Advanced search options to find suppliers. Click Search to view results.',
      title: 'Search Parameters',
      enableBackdrop: true,
      prevStep: 'search-toggle',
      isAsync: true
    }], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    });
  }

  isTourStarted = false;
  getIsTourStartedIndicator() {
    return this.isTourStarted;
  }
  getisTourTriggeredFlag() {
    return this.isTourTriggered;
  }

  setIsTourStartedIndicator(value: boolean) {
    this.isTourStarted = value;
  }

  initCampaignActionTour(Id: any) {

    this.tourService.initialize([{
      anchorId: 'Actions' + Id,
      stepId: 'Actions',
      content: 'Click \'Actions\' to initiate an action on the resolved campaign list',
      title: 'Actions',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
    this.tourService.start$.subscribe(param => {
      this.setIsTourStartedIndicator(true)
    })
  }

  initCampaignActionPageTour() {
    this.tourService.initialize([{
      anchorId: 'expedited_subscription',
      stepId: 'expedited_subscription',
      content: 'This action lets the buyer subscribe supplier to multiple applications.',
      title: 'Expedited Subscription',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      nextStep: 'qualified_subscription',
      isAsync: true
    }, {
      anchorId: 'qualified_subscription',
      stepId: 'qualified_subscription',
      content: 'This action lets the buyer subscribe supplier to application, assign a form and assign an approver.',
      title: 'Qualified Subscription',
      enableBackdrop: true,
      prevStep: 'expedited_subscription',
      nextStep: 'assign_form',
      isAsync: true
    }, {
      anchorId: 'assign_form',
      stepId: 'assign_form',
      content: 'This action lets the buyer assign a form to the supplier',
      title: 'Assign form',
      enableBackdrop: true,
      prevStep: 'qualified_subscription',
      nextStep: 'Cancel',
      isAsync: true
    },
    {
      anchorId: 'Cancel',
      stepId: 'Cancel',
      content: '\'Cancel\' disregards any changes on this campaign and will guide you back to the campaigns board',
      title: 'Cancel',
      enableBackdrop: true,
      prevStep: 'qualified_subscription',
      nextStep: 'start',
      isAsync: true
    },
    {
      anchorId: 'start',
      stepId: 'start',
      content: '\'START\' initiates an action on the campaign. It will be enabled only after the buyer selects one of the actions for the campaign',
      title: 'Start',
      enableBackdrop: true,
      prevStep: 'Cancel',
      isAsync: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    });
    this.setupTourEventListeners();
  }

  setupTourEventListeners() {
    const actionMap: { [key: string]: () => void } = {
      'expedited_subscription': () => this.triggerYourAction('expedited_subscription'),
      'qualified_subscription': () => this.triggerYourAction('qualified_subscription'),
      'assign_form': () => this.triggerYourAction('assign_form')
    };

    this.tourService.stepShow$.subscribe(params => {
      const stepId = params.step.stepId;
      if (stepId && actionMap[stepId]) {
        actionMap[stepId]();
      }
    });
  }

  triggerYourAction(elementId: string) {
    const actionButton = document.querySelector(`#${elementId}`) as HTMLButtonElement | null;
    if (actionButton) {
      actionButton.click();
      this.isTourTriggered = false;
    }
  }

  initCampaignResolveTour(Id: any) {

    this.tourService.initialize([{
      anchorId: 'Resolve' + Id,
      stepId: 'Resolve',
      content: 'After a successful campaign upload, the buyer needs to validate each supplier from the matched list. To validate a supplier, click \'RESOLVE\'',
      title: 'Resolve',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      isAsync: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
  }
 

  initSkippedTabTour() {
    this.tourService.initialize([{
      anchorId: 'skipTab',
      stepId: 'skipTab',
      content: 'All records skipped from the Data Review and Successful Actions tabs are listed here.',
      title: 'Skip',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      isAsync: true,
      nextStep: 'skipDownload',
    }, {
      anchorId: 'skipDownload',
      stepId: 'skipDownload',
      content: 'All records skipped from the Data Review and Successful Actions tabs are listed here.',
      title: 'Download Skip data',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      isAsync: true,
      prevStep: 'skipTab'
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
  }
  initCampaignResolvePageTour(ID: any) {
    this.tourService.initialize([{
      anchorId: 'successful_actions',
      stepId: 'successful_actions',
      content: 'After uploading a campaign, review the matched supplier list.',
      title: 'Successful Actions',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      nextStep: 'resolve_accordion',
      isAsync: true
    },
      {
      anchorId: 'resolve_accordion' + ID,
      stepId: 'resolve_accordion',
      content: 'Click the accordion to see the matched list of suppliers.',
      title: 'Accordion',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      nextStep: 'first_row',
      prevStep: 'successful_actions',
      isAsync: true
    },
    {
      anchorId: 'first_row' + ID,
      stepId: 'first_row',
      content: `
      <ol>
        <li> This is the original entry provided in the campaign template.</li>
        <li> Selecting this row without the Exostar ID will initiate an invitation to a new supplier.</li>
        <li> This will show a black check icon when selected.</li>
      </ol>
    `,
      title: 'First Row',
      enableBackdrop: true,
      delayBeforeStepShow: 1000,
      prevStep: 'resolve_accordion',
      nextStep: 'Skip_Checkbox',
      isAsync: true
    },
    {
      anchorId: 'Skip_Checkbox' + ID,
      stepId: 'Skip_Checkbox',
      content: 'If needed, you may skip a record. Skipped records will be stored in the Skipped Records tab for later review.',
      title: 'Skip checkbox',
      enableBackdrop: true,
      prevStep: 'second_row',
      isAsync: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    });
    this.setupResolveTourEventListeners(ID);
  }

   initCampaignDataResolutionTour(Id: any) {

    this.tourService.initialize([{
      anchorId: 'Resolve' + Id,
      stepId: 'Resolve',
      content: 'After uploading your CSV file, review and edit the mapped fields. If any errors are highlighted, correct them before proceeding. You may also adjust valid fields if needed. To validate the records, click RESOLVE.',
      title: 'Resolve',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      isAsync: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
  }
  
  initCampaignDataResolutionPageTour(Id?: any) {
      this.tourService.initialize([{
      anchorId: 'data_resolution_tab',
      stepId: 'data_resolution_tab',
      content: 'Review the mapped fields before proceeding. Fix highlighted errors and update valid fields if needed by clicking on the field to edit.',
      title: 'Data Resolution Tab',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      nextStep: 'revalidate_first_row',
      isAsync: true
    },{
      anchorId: 'revalidate_first_row'+ Id,
      stepId: 'revalidate_first_row',
      content: 'Click the Revalidate icon to confirm the record.',
      title: 'Revalidate',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      nextStep: 'skip_checkbox',
      prevStep: 'revalidate_first_row',
      isAsync: true
    },{
      anchorId: 'skip_checkbox' + Id,
      stepId: 'skip_checkbox',
      content: 'You may also skip a record by selecting the checkbox. All skipped records will be available in the Skipped Records tab for review.',
      title: 'Skip checkbox',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      prevStep: 'revalidate_first_row',
      isAsync: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    })
  }

  setupResolveTourEventListeners(ID: any) {

    this.tourService.stepHide$.subscribe((step) => {
      if (step.step.stepId === 'resolve_accordion') {
        this.isTourTriggered = true;
        this.triggerYourAction('resolve_accordion' + ID)
      }
    });
  }


  initGroupActionItems() {
    this.tourService.initialize([{
      anchorId: 'open_group',
      stepId: 'open_group',
      content: 'Click the button to see group details.',
      title: 'Open',
      enableBackdrop: true,
      allowUserInitiatedNavigation: true,
      nextStep: 'edit_group',
      isAsync: true
    },
    {
      anchorId: 'edit_group',
      stepId: 'edit_group',
      content: 'Click the button to edit the group details',
      title: 'Edit',
      enableBackdrop: true,
      prevStep: 'open_group',
      nextStep: 'invite_user',
      isAsync: true
    },
    {
      anchorId: 'invite_user',
      stepId: 'invite_user',
      content: 'Click the button to invite suppliers.',
      title: 'Invite',
      enableBackdrop: true,
      prevStep: 'edit_group',
      nextStep: 'clone_group',
      isAsync: true
    },
    {
      anchorId: 'clone_group',
      stepId: 'clone_group',
      content: 'Click the button to clone the group.',
      title: 'Clone',
      enableBackdrop: true,
      prevStep: 'invite_user',
      nextStep: 'delete_group',
      isAsync: true
    },
    {
      anchorId: 'delete_group',
      stepId: 'delete_group',
      content: 'Click the button to delete group',
      title: 'Delete',
      enableBackdrop: true,
      prevStep: 'clone_group',
      isAsync: true
    }
    ], {
      enableBackdrop: true,
      endBtnTitle: 'Finish',
      nextBtnTitle: '',
      prevBtnTitle: ''
    });
  }
  getTourService() {
    return this.tourService;
  }

  set tourStart(hasStarted: boolean) {
    this.tourStart = hasStarted;
  }
  get tourStart() {
    return this.tourStart;
  }

}
